
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Mail, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Member {
  id: string;
  user_id: string | null;
  role: string;
  invited_at: string | null;
  joined_at: string | null;
  invited_email?: string;
  status?: 'pending' | 'active';
  profiles?: {
    full_name: string | null;
    email: string | null;
  } | null;
}

interface EnhancedMemberInvitationsProps {
  organizationId: string;
}

export function EnhancedMemberInvitations({ organizationId }: EnhancedMemberInvitationsProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchMembers();
  }, [organizationId]);

  const fetchMembers = async () => {
    try {
      const { data, error } = await supabase
        .from("organization_memberships")
        .select(`
          *,
          profiles (full_name, email)
        `)
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Transform the data to handle the left join properly
      const transformedMembers = data?.map((member: any) => ({
        ...member,
        profiles: member.profiles || null
      })) || [];
      
      setMembers(transformedMembers);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const generateInviteLink = (organizationId: string, inviteId: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/team/join?org=${organizationId}&invite=${inviteId}`;
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get organization and user details for the invitation
      const { data: organization, error: orgError } = await supabase
        .from("organizations")
        .select("name")
        .eq("id", organizationId)
        .single();

      if (orgError) throw orgError;

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();

      if (profileError) throw profileError;

      // Send email invitation
      const { data, error } = await supabase.functions.invoke('send-team-invitation', {
        body: {
          organizationId,
          email: inviteEmail,
          role: inviteRole,
          inviterName: profile.full_name || user.email || 'Team Admin',
          organizationName: organization.name
        }
      });

      if (error) throw error;

      toast({
        title: "Invitation Sent",
        description: `Email invitation sent to ${inviteEmail}. They'll receive an email with instructions to join your organization.`,
      });

      setInviteEmail("");
      setInviteRole("member");
      fetchMembers();
    } catch (error: any) {
      console.error("Error sending invitation:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from("organization_memberships")
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      toast({
        title: "Member Removed",
        description: "Member has been removed from the organization",
      });

      fetchMembers();
    } catch (error: any) {
      console.error("Error removing member:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove member",
        variant: "destructive",
      });
    }
  };

  const copyInviteLink = async (memberId: string) => {
    const inviteLink = generateInviteLink(organizationId, memberId);
    await navigator.clipboard.writeText(inviteLink);
    setCopiedInvite(memberId);
    setTimeout(() => setCopiedInvite(null), 2000);
    
    toast({
      title: "Invite Link Copied",
      description: "The invitation link has been copied to your clipboard",
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Invite Team Members</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                They don't need a CSO Agent account yet - we'll create one when they accept the invitation
              </p>
            </div>

            <div>
              <Label htmlFor="role">Role</Label>
              <Select
                value={inviteRole}
                onValueChange={(value: "admin" | "member") => setInviteRole(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" disabled={isLoading}>
              <Mail className="h-4 w-4 mr-2" />
              {isLoading ? "Sending Invitation..." : "Send Email Invitation"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Team Members & Invitations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div>
                    <p className="font-medium">
                      {member.joined_at ? 
                        (member.profiles?.full_name || member.profiles?.email || `User ${member.user_id?.slice(0, 8)}...`) : 
                        (member.invited_email || 'Pending Invitation')
                      }
                    </p>
                    <p className="text-sm text-gray-500">
                      {member.joined_at ? 
                        `Joined ${new Date(member.joined_at).toLocaleDateString()}` : 
                        `Invited ${new Date(member.invited_at || "").toLocaleDateString()}`
                      }
                    </p>
                  </div>
                  <Badge variant={member.role === "owner" ? "default" : "secondary"}>
                    {member.role}
                  </Badge>
                  {!member.joined_at && (
                    <Badge variant="outline">Pending</Badge>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {!member.joined_at && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyInviteLink(member.id)}
                    >
                      {copiedInvite === member.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  )}
                  
                  {member.role !== "owner" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(member.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {members.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                No team members yet. Invite your first colleague above!
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
